import { useEffect, useState } from 'react';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import TileCard from '../components/TileCard';

/**
 * Stipend Usage tile — monthly stipend / MTD spent / remaining with a
 * progress bar. Pulls /api/expenses/stipend-info, which already returns
 * the three numbers for the requested user (defaults to the caller).
 *
 * Empty state when the user has no monthly_stipend configured (0/0) —
 * renders "No stipend set up yet" instead of a $0/$0 progress bar.
 */
export default function StipendUsageTile({ userId: userIdProp }) {
  const { user } = useAuth();
  const userId = userIdProp || user?.id;

  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    if (!userId) { setState({ loading: false, error: null, data: null }); return; }
    let cancelled = false;
    api.getStipendInfo(userId)
      .then(d => { if (!cancelled) setState({ loading: false, error: null, data: d }); })
      .catch(err => { if (!cancelled) setState({ loading: false, error: err?.message || 'failed', data: null }); });
    return () => { cancelled = true; };
  }, [userId]);

  const d = state.data;
  const isEmpty = !state.loading && !state.error && (!d || !d.monthly_stipend);
  const pct = d && d.monthly_stipend > 0
    ? Math.min(100, Math.max(0, Math.round((d.mtd_spent / d.monthly_stipend) * 100)))
    : 0;
  const barColor = pct >= 100 ? '#DC2626' : pct >= 80 ? '#F59E0B' : '#10B981';

  return (
    <TileCard
      title="Stipend Usage"
      loading={state.loading}
      error={state.error}
      empty={isEmpty}
      emptyLabel="No stipend set up yet"
      actionHref="/sales/expenses"
      actionLabel="Expenses →"
    >
      {d && d.monthly_stipend > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <div>
              <div className="dash-statcard-value" style={{ fontSize: 28 }}>
                {fmt(d.mtd_spent)}
              </div>
              <div className="dash-statcard-sublabel">
                of {fmt(d.monthly_stipend)} monthly
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: pct >= 100 ? '#DC2626' : '#111827' }}>
                {pct}%
              </div>
              <div className="dash-statcard-sublabel" style={{ textAlign: 'right' }}>
                {d.remaining >= 0 ? fmt(d.remaining) + ' left' : fmt(Math.abs(d.remaining)) + ' over'}
              </div>
            </div>
          </div>
          <div style={{
            height: 8, background: '#F3F4F6', borderRadius: 999, overflow: 'hidden', marginTop: 8,
          }}>
            <div style={{
              width: `${Math.min(100, pct)}%`,
              height: '100%',
              background: barColor,
              transition: 'width 0.3s ease',
            }} />
          </div>
        </>
      )}
    </TileCard>
  );
}

function fmt(n) {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
